import { GLOBAL } from '@/store/types'
import router from '@/router'
import utils from '@/utils/utils'
import storage from '@/utils/storage'
import EventBus from 'login/EventBus'
import strings from '../../strings'

const Global = {
  namespaced: true,
  state: {
    blockDialog: false,
    pairDialogHelper: false,
    initialSettingsLoaded: false,
    windowWidth: 0,
    windowHeight: 0,
    initialSettings: {
      desks: {},
      restaurants: {},
      charteredBuses: {},
      parkings: {},
      user: {},
      lockers: {},
      hasMonitoring: false,
      hasAccessControl: false,
      hasComfort: false,
      corporateNotice: {}
    }
  },
  getters: {
    hasServiceDesk (state) {
      return state.initialSettings.hasServiceDesk
    },
    hasWorkspace(state){
      return state.initialSettings.hasWorkspace
    },
    hasBookingForGuests (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.hasBookingForGuests
    },
    hasBookingForRestaurant (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.restaurants.hasBooking
    },
    hasBookingForChartered (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.charteredBuses.hasBooking
    },
    hasBookingForParking (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.parkings.hasBooking
    },
    hasBookingForLocker (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.lockers.hasBooking
    },
    hasBookingForDesk (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.desks.hasBooking
    },
    hasBookingForTeam(state) {
      if (!state.initialSettings.featureFlags.find(item => item.name === strings.featureFlags.inviteTeam)) {
        return false
      }

      return state.initialSettings.hasWorkspace && state.initialSettings.desks.hasBooking
    },
    hasBookingForWorkspace(state) {
      if(!state.initialSettings.hasWorkspace){
        return false
      }

      return state.initialSettings.desks.hasBooking ||
        state.initialSettings.restaurants.hasBooking ||
        state.initialSettings.parkings.hasBooking ||
        state.initialSettings.lockers.hasBooking
    },
    hasMonitoring (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.desks.hasMonitoring
    },
    hasMapWorkspaceNames (state) {
      return state.initialSettings.hasMapWorkspaceNames
    },
    hasCostCenter (state) {
      return state.initialSettings.hasCostCenter
    },
    hasCheckinByTablet (state) {
      return state.initialSettings.desks.hasCheckinByTablet
    },
    hasPinCheckIn (state) {
      return state.initialSettings.rooms.hasPinCheckIn
    },
    hasWayfinding (state) {
      return state.initialSettings.desks.hasWayfinding
    },
    hasInformVehicleOnParkingBooking (state) {
      return state.initialSettings.parkings.hasInformVehicleOnBooking
    },
    maximumAmountOfVehicles (state) {
      return state.initialSettings.parkings.maximumAmountOfVehicles
    },
    minimumBookingInterval (state) {
      return state.initialSettings.minimumBookingInterval
    },
    maximumBookingInterval (state) {
      return state.initialSettings.maximumBookingInterval
    },
    hasAccessControl (state) {
      return state.initialSettings.hasAccessControl
    },
    hasComfort (state) {
      return state.initialSettings.hasComfort
    },
    initialSettingsSiteConfigs (state) {
      return state.initialSettings.sites ? state.initialSettings.sites : []
    },
    hasBookingFeatures (state ) {
      return state.initialSettings.desks ? (
        state.initialSettings.desks.hasBooking ||
        state.initialSettings.hasMeetingSpace ||
        state.initialSettings.restaurants.hasBooking ||
        state.initialSettings.charteredBuses.hasBooking ||
        state.initialSettings.parkings.hasBooking ||
        state.initialSettings.lockers.hasBooking) : false
    },
    hasBookingForRoom(state) {
      return state.initialSettings.hasMeetingSpace
    },
    //Check product with meta feature on route
    hasProduct (state, getters) {
      return {
        hasBookingForDesk: state.initialSettings.desks.hasBooking,
        hasBookingForParking: state.initialSettings.parkings.hasBooking,
        hasBookingFeatures: getters.hasBookingFeatures,
        hasWayfinding: state.initialSettings.desks.hasWayfinding
      }
    },
    //To build book menu
    hasBookingFor(state, getters){
      return {
        'desks': getters.hasBookingForDesk,
        'team': getters.hasBookingForTeam,
        'rooms': state.initialSettings.hasMeetingSpace,
        'roomNow': state.initialSettings.hasMeetingSpace,
        'restaurant': getters.hasBookingForRestaurant,
        'charteredBuses': getters.hasBookingForChartered,
        'parking': getters.hasBookingForParking,
        'lockers': getters.hasBookingForLocker
      }
    },
    blockDialog (state) {
      return state.blockDialog
    },
    isMobile (state) {
      return state.windowWidth < 961
    },
    isTeamBookingOnlyByTeamManagers(state) {
      return state.initialSettings.desks.isTeamBookingOnlyByTeamManagers
    },
    hasValidInitialUserSettings (state, getters) {
      const favoriteSiteIsValid = state.initialSettings.user.workspace.favoriteSiteId !== null
      const favoriteLocationIsValid = state.initialSettings.user.workspace.favoriteLocationId !== null
      const workingTimeIsValid = state.initialSettings.user.workspace.startWorkingTime !== null && state.initialSettings.user.workspace.finishWorkingTime !== null
      const pinIsValid = !state.initialSettings.desks.hasCheckinByTablet || (state.initialSettings.desks.hasCheckinByTablet && state.initialSettings.user.workspace.pin !== null)
      const costCenterIsValid = !state.initialSettings.hasCostCenter || (state.initialSettings.hasCostCenter && state.initialSettings.user.costCenter !== null)

      const favoriteLocationFreeroomIsValid = state.initialSettings.user.meetingSpace.favoriteLocationId !== null
      const pinFreeroomIsValid = !state.initialSettings.rooms.hasPinCheckIn || (state.initialSettings.rooms.hasPinCheckIn && state.initialSettings.user.meetingSpace.pin !== null)

      if(getters.hasBookingForRoom){
        if(getters.hasBookingFeatures){
          return favoriteSiteIsValid && favoriteLocationIsValid && workingTimeIsValid && costCenterIsValid && pinIsValid && favoriteLocationFreeroomIsValid && pinFreeroomIsValid
        }
        return favoriteLocationFreeroomIsValid && favoriteLocationFreeroomIsValid
      }
      return favoriteSiteIsValid && favoriteLocationIsValid && workingTimeIsValid && costCenterIsValid && pinIsValid
    },
    featureFlags (state) {
      return state.initialSettings.featureFlags
    }
  },
  mutations: {
    [GLOBAL.SET_INITIAL_SETTINGS] (state, initialSettings) {
      state.initialSettings = initialSettings
    },
    [GLOBAL.INITIAL_SETTINGS_LOADED] (state, value) {
      state.initialSettingsLoaded = value
    },
    [GLOBAL.SET_BLOCK_DIALOG] (state, show){
      state.blockDialog = show
    },
    [GLOBAL.SET_PAIR_DIALOG_HELPER] (state, pairDialogHelper){
      state.pairDialogHelper = pairDialogHelper
    },
    [GLOBAL.SET_WINDOW_WIDTH] (state, value) {
      state.windowWidth = value
    },
    [GLOBAL.SET_WINDOW_HEIGHT] (state, value) {
      state.windowHeight = value
    }
  },
  actions: {
    // eslint-disable-next-line no-empty-pattern
    getInitialSettings: function ({}) {
      EventBus.publish("getInitialSettings")
    },
    updateInitialSettings: function ({commit, dispatch}) {
      return new Promise((resolve) => {
        let response = storage.getFromLocalStorage('settings')
        commit(GLOBAL.SET_INITIAL_SETTINGS, response)
        dispatch('Client/setClientDomains', response.domains, {root: true})
        commit(GLOBAL.INITIAL_SETTINGS_LOADED, true)
        resolve()
      })
    },
    // eslint-disable-next-line no-empty-pattern
    showMessage ({}, obj) {
      EventBus.publish("setMessage", obj)
    },
    // eslint-disable-next-line no-empty-pattern
    showLoading ({}) {
      EventBus.publish("showLoading")
    },
    // eslint-disable-next-line no-empty-pattern
    hideLoading ({}) {
      EventBus.publish("hideLoading")
    },
    hasFeatureForRoute({getters}, path){
      for (let route of router.options.routes) {
        if (route.path === path) {
          return utils.checkFeatureInRoute(route.meta.features, getters.hasProduct)
        }
      }
      return false
    },
    setBlockDialog({commit}, show){
      commit(GLOBAL.SET_BLOCK_DIALOG, show)
    },
    setPairDialogHelper({commit}, show){
      commit(GLOBAL.SET_PAIR_DIALOG_HELPER, show)
    },
    setWindowWidth({commit}, value) {
      commit(GLOBAL.SET_WINDOW_WIDTH, value)
    },
    setWindowHeight({commit}, value) {
      commit(GLOBAL.SET_WINDOW_HEIGHT, value)
    }
  }
}

export default Global
